/**
 * IMPORTS
 */
import {subscribeCustomerTagAdded}
    from 'src/aggregates/customers/subscriptions/utils';
import {subscribeCustomerTagRemoved}
    from 'src/aggregates/customers/subscriptions/utils';
import {unsubscribeCustomerTagAdded}
    from 'src/aggregates/customers/subscriptions/utils';
import {unsubscribeCustomerTagRemoved}
    from 'src/aggregates/customers/subscriptions/utils';
import {fetchKanbans} from 'src/aggregates/user/api';
import {replaceKanbanMetadata} from 'src/aggregates/user/api';
import {fetch as getUser} from 'src/aggregates/user/selectors';
import {getKanbanMetadata} from 'src/aggregates/user/selectors';
import {KANBAN_UPDATED} from 'src/aggregates/user/subscriptions';
import {reduceKanbanUpdated}
    from 'src/aggregates/user/subscriptions/reconcile';
import {logger} from 'src/logger';
import graphis from 'src/services/graphis';


/**
 * TYPES
 */
import {IKanbanUpdated} from 'src/aggregates/user/subscriptions/index.d';
import {IPayload} from 'src/services/graphis/index.d';


/**
 * CODE
 */

/**
 * I subscribe on kanban-updated event.
 *
 * :returns: nothing
 */
function subscribeKanbanUpdated(): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // subscribe to kanban-updated topic
    graphis.subscribe<IPayload<IKanbanUpdated>>(
        (payload) =>
        {
            // get payload columns
            const columns = payload?.data?.kanbanUpdated ?? null;

            // no columns data: ignore malformed payload and return
            if (columns === null)
            {
                return;
            }

            // get kanban metadata
            const metadata = getKanbanMetadata();

            // reconcile received columns with stored metadata
            const {byTag, tagsToSubscribe, tagsToUnsubscribe} =
                reduceKanbanUpdated(columns, metadata.byTag);

            // unsubscribe removed columns tags
            for (const tag of tagsToUnsubscribe)
            {
                unsubscribeCustomerTagAdded(tag);
                unsubscribeCustomerTagRemoved(tag);
            }

            // subscribe new columns tags
            for (const tag of tagsToSubscribe)
            {
                subscribeCustomerTagAdded(tag);
                subscribeCustomerTagRemoved(tag);
            }

            // update kanbans
            replaceKanbanMetadata(byTag);
        },
        KANBAN_UPDATED,
        undefined,
        (error) =>
        {
            // log subscription error
            logger.error(
                'CVJPZ0100E',
                {
                    company,
                    name,
                    reason: error?.message ?? String(error),
                    topic: KANBAN_UPDATED.getTopic(),
                    user,
                },
            );

            // re-fetch kanbans to restore data and subscriptions
            fetchKanbans();
        },
    );

    // log subscription
    logger.info(
        'CVJPZ0098I',
        {
            company,
            name,
            topic: KANBAN_UPDATED.getTopic(),
            user,
        },
    );
}


/**
 * I unsubscribe on kanban-updated event.
 *
 * :returns: nothing
 */
function unsubscribeKanbanUpdated(): void
{
    // get user company
    const {company, id: user, name} = getUser();

    // unsubscribe kanban-updated topic
    graphis.unsubscribe(KANBAN_UPDATED);

    // log unsubscribe
    logger.info(
        'CVJPZ0099I',
        {
            company,
            name,
            topic: KANBAN_UPDATED.getTopic(),
            user,
        },
    );
}


/**
 * EXPORTS
 */
export {
    subscribeKanbanUpdated,
    unsubscribeKanbanUpdated,
};
